/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useMemo, useState } from 'react';

import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { FiInfo } from 'react-icons/fi';
import { isExpired } from 'react-jwt';

import ConfirmationModal from '../../../components/Dialogs/ConfirmationModal';
import { Color } from '../../../components/Form/Button';
import Estimate from "../../../components/Generic/Estimate";
import SkeletonDetails from '../../../components/Generic/LoadingSkeleton';
import PackingMaterials from "../../../components/Generic/PackingMaterials"
import { NotificationToast } from '../../../components/Generic/Toast/NotificationToast';
import UITabMaterial, { PanelProps } from '../../../components/Generic/UITabMaterial';
import LoadingScreen from '../../../components/LoadingScreen';
import TitlePage from '../../../components/TitlePage';
import { AppConfig } from "../../../constants";
import { Error } from '../../../models/error';
import { Location } from '../../../models/location';
import { Sales } from '../../../models/sales';
import JobEstimateCustomerService from '../../../services/job.estimate.customer.service';
import { EstimatedQuoteStatus } from '../../../util/enums';
import FormOnViewMode from '../../private/Moving/Quote/Maintenance/Details/Form/ViewMode';
import { ActionButtons } from './components/Actions';
import { EmptyEstimatedQuote } from './components/EmptyEstimatedQuote';
import { ExpiredTokenComponent } from './components/ExpiredToken';
import { Footer } from "./components/Footer";
import { Header } from './components/Header';
import { Inventory } from './components/Inventory';
import { ResponseTemplate } from './components/ResponseTemplate';
import { TabHeader } from './components/TabHeader';
import useStyles, { Wrapper, HeaderRightText, TextAreaWrapper, PageWrapper } from './styles';

const ShipeeziLogo = AppConfig.appLogo;

enum TabSelected {
  DETAILS = "details",
  INVENTORY = "inventory",
  MATERIAL = "material",
  QUOTE = "quote",
}

export type Decoded = {
  customerId: number;
  jobId: number;
}

const EstimateQuote = () => {
  const classes = useStyles();
  const { t: translate } = useTranslation();

  // eslint-disable-next-line @typescript-eslint/naming-convention
  const [token, setToken] = useState<string>();
  const [job, setJob] = useState<Sales>();
  const [selectedPriceId, setSelectedPriceId] = useState<number>();
  const [paymentMethod, setPaymentMethod] = useState<string>("");

  const [tokenIsExpired, setTokenIsExpired] = useState<boolean>(false);

  const [openConfirmationModal, setOpenConfirmationModal] = useState<boolean>(false);
  const [remark, setRemark] = useState<string>("");
  const [modalType, setModalType] = useState<"success"| "error"|"warning">();
  const [onActionChange, setOnActionChange] = useState<string>("");

  const { isFetching } = useQuery(
    ["job.item", token],
    () => JobEstimateCustomerService.getExternalData(String(token)), {
      enabled: !!token?.trim()?.length && !tokenIsExpired,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) {
          if(response?.data?.isExpired) {
            setTokenIsExpired(true);
          } else {
            setJob(response.data);
            if (response.data.estimate?.paymentMethod?.trim()?.length) {
              setPaymentMethod(response.data.estimate.paymentMethod ?? "");
            } else setPaymentMethod(response.data.paymentMethod ?? "");
          }
        }
      }
    }
  )

  const { mutate: approveEstimate, isLoading: loadingApprove } = useMutation(["approve.estimate"], () => JobEstimateCustomerService.approveEstimate({
    jobId: job?.id,
    customerId: job?.destination?.customer.id,
    jobEstimateId: job?.estimate?.id,
    estimatePriceId: selectedPriceId,
    paymentMethod,
  }), {
    onSuccess: () => {
      NotificationToast.success(translate("operationPerformedSuccessfully"))
      setOpenConfirmationModal(false);
      setOnActionChange(EstimatedQuoteStatus.APPROVED);
    },
    onError: (error: AxiosError<Error>) => {
      NotificationToast.error(error.message)
    }
  })
  const { mutate: sendToReview, isLoading: loadingReview } = useMutation(["approve.estimate"], () => JobEstimateCustomerService.sendToReview({
    jobId: job?.id,
    customerId: job?.destination?.customer.id,
    jobEstimateId: job?.estimate?.id,
    remark,
  }), {
    onSuccess: () => {
      NotificationToast.success(translate("operationPerformedSuccessfully"))
      setOpenConfirmationModal(false)
      setRemark("")
      setOnActionChange(EstimatedQuoteStatus.IN_REVIEW);
    },
    onError: (error: AxiosError<Error>) => {
      NotificationToast.error(error.message)
    }
  })
  const { mutate: declineEstimate, isLoading: loadingDecline } = useMutation(["approve.estimate"], () => JobEstimateCustomerService.declineEstimate({
    jobId: job?.id,
    customerId: job?.destination?.customer.id,
    jobEstimateId: job?.estimate?.id,
    remark
  }), {
    onSuccess: () => {
      NotificationToast.success(translate("operationPerformedSuccessfully"))
      setOpenConfirmationModal(false);
      setRemark("");
      setOnActionChange(EstimatedQuoteStatus.DECLINED);
    },
    onError: (error: AxiosError<Error>) => {
      NotificationToast.error(error.message)
    }
  })

  const isLoading = loadingApprove || loadingDecline || loadingReview;

  const titleRight = useMemo(() => {
    const title = `Quote #${job?.reference ?? ""} | `

    if (job?.customer) {

      if (job?.customer?.fullName) return `${title  }${job.customer.fullName}`;

      return `${title  }${job.customer.name ?? ""} ${job.customer.lastName}`;

    }

    return `Quote #${job?.reference ?? ""}`
  }, [job])

  const modalTitle = useMemo(() => {
    if (modalType === "success") return "Approve estimate";
    if (modalType === "error") return "Decline estimate";
    if (modalType === "warning") return "Send estimate to review";

    return "";
  }, [modalType])

  const modalContent = () => {
    if (modalType === "success") {
      return (
        <span className={classes.modalContent}>{translate("youAreAboutToApproveThisEstimateAreYouSure")}</span>
      )
    }

    return (
      <TextAreaWrapper>
        <span className={classes.modalContent}>{modalType === "error" ? translate("reasonForDeclining") : translate("reasonForReview")}</span>
        <textarea
            placeholder={translate("reason")}
            value={remark}
            onChange={(event) => setRemark(event.target.value)}
          />
      </TextAreaWrapper>
    )
  }

  const saveButtonColor = useMemo(() => {
    if (modalType === "warning") return Color.SECONDARY;
    if (modalType === "error") return Color.ERROR;
    if (modalType === "success") return Color.SUCCESS;
  }, [modalType])

  const saveButtonText = useMemo(() => {
    if (modalType === "success") return translate("approve");
    if (modalType === "error") return translate("decline");
    if (modalType === "warning") return translate("sendToReview");
  }, [modalType])

  const onSend = (modalType: "success"| "error"|"warning") => {
    setModalType(modalType);
    setOpenConfirmationModal(true)
  }

  const detailsPanel: PanelProps = {
    key: TabSelected.DETAILS,
    className: classNames(classes.uiTab, classes.tabDetails),
    loading: isFetching,
    menuItem: translate("details"),
    skeleton: () => <SkeletonDetails />,
    render: () => (
      <>
        <TabHeader title={translate("details")} job={job} />
        <FormOnViewMode quote={job} onCustomerView />
        {job && (
          <ActionButtons
            loadings={{ approve: loadingApprove, decline: loadingDecline, review: loadingReview}}
            onSend={onSend}
          />
        )}
      </>
    )
  };

  const inventoryPanel: PanelProps = {
    key: TabSelected.INVENTORY,
    className: classNames(classes.uiTab, classes.tabDetails),
    menuItem: translate("inventory"),
    loading: isFetching,
    skeleton: () => <SkeletonDetails />,
    render: () => (
      <>
        <Inventory job={job}  />
        {job && (
          <ActionButtons
            loadings={{ approve: loadingApprove, decline: loadingDecline, review: loadingReview}}
            onSend={onSend}
          />
        )}
      </>
    )
  };

  const materialPanel: PanelProps = {
    key: TabSelected.MATERIAL,
    className: classNames(classes.uiTab, classes.tabDetails),
    menuItem: translate("materials"),
    loading: isFetching,
    skeleton: () => <SkeletonDetails />,
    render: () => (
      <>
        <PackingMaterials isOnViewMode onCustomerView inventory={job?.inventory} />
        {job && (
          <ActionButtons
            loadings={{ approve: loadingApprove, decline: loadingDecline, review: loadingReview}}
            onSend={onSend}
          />
        )}
      </>
    )
  };

  const quotePanel: PanelProps = {
    key: TabSelected.QUOTE,
    className: classNames(classes.uiTab, classes.tabDetails),
    menuItem: translate("quote"),
    loading: isFetching,
    skeleton: () => <SkeletonDetails />,
    render: () => {
      return job ? (
        <>
          <TabHeader title={translate("quote")} job={job}  />
          <Estimate
            isOnViewMode
            estimate={job?.estimate}
            agentId={job?.agentId}
            agentSelected={job?.agent}
            inventory={job?.inventory ?? []}
            destinationAddresses={(job?.destination?.locations || []) as Location[]}
            originAddresses={(job?.origin?.locations || []) as Location[]}
            taskDates={job?.taskDates ?? []}
            onCustomerView
            selectedPriceId={selectedPriceId}
            setSelectedPriceId={setSelectedPriceId}
            externalList={{
              destinationList: job?.destinationList ?? [],
              paymentMethodList: job?.paymentMethodList
            }}
            setPaymentMethod={setPaymentMethod}
            paymentMethod={paymentMethod}
            currency={job?.company?.currency}
          />
          <ActionButtons
            loadings={{ approve: loadingApprove, decline: loadingDecline, review: loadingReview}}
            onSend={onSend}
          />
        </>
      ) : (
        <EmptyEstimatedQuote />
      )}
  };

  const handleSubmit = () => {
    if (modalType === "success") {
      if (!selectedPriceId) {
        NotificationToast.warning(translate("youHaveToChooseAPriceInTheQuoteTab"))

        return;
      }

      if (!paymentMethod) {
        NotificationToast.warning(translate("youHaveToChooseAPaymentMethodInTheQuoteTab"))

        return;
      }

      approveEstimate();
    }
    else if (modalType === "error") declineEstimate();
    else sendToReview();
  }

  useEffect(() => {
    try {
      const currentToken = window.location.href.split("?token=")[1];

      if (currentToken) {
        const tokenExpired = isExpired(currentToken);

        setToken(currentToken);

        setTokenIsExpired(tokenExpired);

        if (!tokenExpired) {
          setToken(currentToken);
        }
      }
    } catch (error) {
      console.error(error)
    }
  }, []);

  return (
    <PageWrapper>
      <LoadingScreen
        showing={isFetching}
        message={translate("yourEstimateQuoteInformationIsLoading")}
      />

      <ConfirmationModal
        open={openConfirmationModal}
        closeModal={() => {
          if (!isLoading) {
            setRemark("");
            setOpenConfirmationModal(false)
          }
        }}
        customColorSave={saveButtonColor}
        textSubmit={saveButtonText}
        type={modalType}
        title={modalTitle}
        submit={handleSubmit}
        loading={isLoading}
        width="570"
        saveDisabled={modalType === "success" ? false : !remark.length}
        leftIconTitle={modalType === "success" ? <FiInfo color="#12B76A" /> : undefined}
      >
        {modalContent()}
      </ConfirmationModal>

      <Header companyLogo={job?.company?.logo?.mediaLink || ShipeeziLogo} />
      {tokenIsExpired ? (
        <ExpiredTokenComponent />
      ) : (
        <>
          {onActionChange.length ? (
            <ResponseTemplate type={onActionChange}  />
          ) : (
            <>
              <TitlePage
                title={translate("estimatedQuote")}
                right={<HeaderRightText>{titleRight}</HeaderRightText>}
              />

              <Wrapper>
                <UITabMaterial
                  leftPanels={[detailsPanel, inventoryPanel, materialPanel, quotePanel]}
                />
              </Wrapper>
            </>
          )}
        </>
      )}

      {/* TODO: Waiting for confirmation that if this activity will be shown */}
      {/* {openActivityScreen ? <Activity items={onGetHistoryItems()} onClose={() => setOpenActivityScreen(false)} /> : null} */}

      <Footer logo={ShipeeziLogo}/>
    </PageWrapper>
  )
}

export default EstimateQuote;
